import { useRef, useState, type ReactNode } from 'react';
import { canvasToPngBlob, hasMeaningfulStroke, type Point } from '../lib/signature';

/**
 * Kanvas tanda tangan.
 *
 * Memakai Pointer Events, bukan mouse+touch terpisah: satu jalur kode untuk jari, stylus, dan
 * tetikus. `touch-action: none` pada elemen mencegah browser menggulir halaman saat orang
 * menandatangani — tanpa itu, tanda tangan di ponsel menjadi gerakan menggulir.
 */
export function SignaturePad({
  onCapture,
  disabled,
}: {
  readonly onCapture: (blob: Blob) => void;
  readonly disabled?: boolean;
}): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Point[][]>([]);
  const drawingRef = useRef(false);
  const [hasStroke, setHasStroke] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pointFrom(event: React.PointerEvent<HTMLCanvasElement>): Point {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    // Skala dari ukuran CSS ke ukuran piksel canvas — tanpa ini garis meleset dari ujung jari
    // pada layar ber-DPR tinggi.
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  function start(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (disabled) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    strokesRef.current.push([pointFrom(event)]);
  }

  function move(event: React.PointerEvent<HTMLCanvasElement>): void {
    if (!drawingRef.current || disabled) return;
    const point = pointFrom(event);
    const stroke = strokesRef.current[strokesRef.current.length - 1];
    const ctx = canvasRef.current?.getContext('2d');
    if (!stroke || !ctx) return;

    const previous = stroke[stroke.length - 1];
    stroke.push(point);
    if (previous) {
      ctx.strokeStyle = '#111827';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(previous.x, previous.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }
    setHasStroke(hasMeaningfulStroke(strokesRef.current));
  }

  function end(): void {
    drawingRef.current = false;
  }

  function clear(): void {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokesRef.current = [];
    setHasStroke(false);
    setError(null);
  }

  async function capture(): Promise<void> {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setError(null);
    try {
      onCapture(await canvasToPngBlob(canvas));
    } catch {
      setError('Tanda tangan belum terbaca. Coba tanda tangani ulang.');
    }
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600}
        height={220}
        className="signature-pad"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        aria-label="Area tanda tangan penerima"
      />
      <p className="muted small">Minta penerima menandatangani di dalam kotak.</p>
      {error ? (
        <p className="warning small" role="alert">
          {error}
        </p>
      ) : null}
      <div className="row">
        <button type="button" onClick={clear} disabled={disabled}>
          Hapus
        </button>
        <button
          type="button"
          className="button-primary"
          onClick={() => void capture()}
          disabled={disabled || !hasStroke}
        >
          Pakai tanda tangan ini
        </button>
      </div>
    </div>
  );
}
